import React, { useCallback, useEffect, useState } from 'react';
import apiService from '../services/api.service';
import { useRealtime } from '../contexts/RealtimeContext';
import type {
  Category,
  CategoryGroup,
  Heat,
  HeatParticipant,
  Team,
  WOD,
} from '../types/heats';

/**
 * Couche de données du planning : heats, catégories, groupes, WODs, équipes
 * et participants d'un événement.
 *
 * Pourquoi un hook plutôt que du `useState` dans chaque page : la page Heats et
 * l'écran d'assignation chargeaient les MÊMES données chacun de son côté, dans
 * deux copies d'état qui pouvaient diverger — assigner une équipe dans l'un ne
 * se voyait pas dans l'autre tant qu'on ne rechargeait pas. Les deux écrans
 * n'en font plus qu'un, et cette source unique est ce qui l'a permis.
 *
 * Il porte aussi les mutations d'assignation, pour la même raison : deux copies
 * de ces appels finiraient par diverger comme l'état l'avait fait.
 */
export function useHeats(eventId: string | null | undefined) {
  const [heats, setHeats] = useState<Heat[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [categoryGroups, setCategoryGroups] = useState<CategoryGroup[]>([]);
  const [wods, setWods] = useState<WOD[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [heatParticipants, setHeatParticipants] = useState<Record<string, HeatParticipant[]>>({});
  const [loading, setLoading] = useState(false);

  const loadHeats = useCallback(async () => {
    if (!eventId) return;
    setLoading(true);
    try {
      // Passe par apiService (et non un `fetch` brut comme avant) : c'est lui
      // qui porte l'intercepteur de rafraîchissement du jeton. Avec le fetch
      // brut, une session expirée renvoyait 401 sans jamais tenter le refresh
      // — l'organisateur voyait un planning vide au lieu d'être reconnecté.
      const response = await apiService.get(`/heats?event_id=${eventId}`);
      setHeats(response.data || []);
    } catch (error) {
      console.error('Erreur chargement heats:', error);
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  const loadCategories = useCallback(async () => {
    if (!eventId) return;
    try {
      const response = await apiService.getCategories(eventId);
      setCategories(response.data?.categories || []);
    } catch (error) {
      console.error('Erreur chargement catégories:', error);
    }
  }, [eventId]);

  const loadCategoryGroups = useCallback(async () => {
    if (!eventId) return;
    try {
      const response = await apiService.get(`/events/${eventId}/category-groups`);
      setCategoryGroups(response.data?.category_groups || []);
    } catch (error) {
      console.error('Erreur chargement groupes catégories:', error);
    }
  }, [eventId]);

  const loadWods = useCallback(async () => {
    if (!eventId) return;
    try {
      const response = await apiService.getWODs(eventId);
      setWods(response.data?.wods || []);
    } catch (error) {
      console.error('Erreur chargement WODs:', error);
    }
  }, [eventId]);

  const loadTeams = useCallback(async () => {
    if (!eventId) return;
    try {
      const response = await apiService.get(`/teams?event_id=${eventId}`);
      setTeams(response.data?.teams || response.data || []);
    } catch (error) {
      console.error('Erreur chargement équipes:', error);
    }
  }, [eventId]);

  /** Participants de tous les heats, triés par lane. */
  const loadAllParticipants = useCallback(async () => {
    // `eventId` est désormais requis : la requête porte sur l'événement, plus
    // sur chaque vague. Le garde d'origine ne testait que `heats`.
    if (!eventId || heats.length === 0) return;

    // UNE requête pour tout l'événement.
    //
    // C'était une requête PAR vague, toutes lancées d'un coup : 80 allers-
    // retours sur Farm Race à chaque ouverture du planning, pour une
    // information qui tient en une réponse. Sur un partage de connexion en
    // gymnase, ça se sent. Même forme que le correctif posé le même jour sur
    // l'Organizer, où cette forme épuisait carrément le pool de connexions.
    //
    // Le serveur sert déjà trié par vague puis par couloir — l'assignation
    // affiche les couloirs dans l'ordre.
    const participantsMap: Record<string, HeatParticipant[]> = {};
    try {
      const response = await apiService.getEventHeatParticipants(eventId);
      for (const p of (response.data?.heat_participants || []) as HeatParticipant[]) {
        (participantsMap[p.heat_id] ??= []).push(p);
      }
    } catch (error) {
      console.error('Erreur chargement des participants:', error);
    }
    setHeatParticipants(participantsMap);
  }, [heats, eventId]);

  // ------------------------------------------------------------------
  // Mutations d'assignation
  //
  // Elles vivent ici, et non dans la page, parce que l'assignation et le
  // planning ne font plus qu'un écran : deux copies de ces appels finiraient
  // par diverger, comme les deux copies de l'état l'avaient déjà fait.
  // ------------------------------------------------------------------

  /**
   * Place une équipe dans un heat. Retourne false si l'API a refusé.
   *
   * Le couloir est attribué par le SERVEUR (premier libre, dans la limite de
   * la capacité) : la position du lâcher n'est pas un numéro de couloir, et
   * l'envoyer laissait croire le contraire.
   */
  const assignTeamToHeat = useCallback(async (
    heatId: string, teamId: string,
  ): Promise<boolean> => {
    try {
      await apiService.post(`/heats/${heatId}/participants`, { team_id: teamId });
      return true;
    } catch (error: any) {
      console.error('Erreur assignation:', error);
      throw new Error(error.response?.data?.detail || error.message);
    }
  }, []);

  const removeTeamFromHeat = useCallback(async (heatId: string, teamId: string): Promise<boolean> => {
    try {
      await apiService.delete(`/heats/${heatId}/participants/${teamId}`);
      return true;
    } catch (error: any) {
      console.error('Erreur retrait équipe:', error);
      throw new Error(error.response?.data?.detail || error.message);
    }
  }, []);

  /** Répartition automatique des équipes non placées, dans un périmètre
      (WOD / catégories / jour / plage horaire — vide = toutes les heats
      planifiées). Les heats en cours ou terminées ne sont jamais touchées. */
  const autoAssignTeams = useCallback(async (scope: object = {}): Promise<number> => {
    const response = await apiService.post(`/heats/auto-assign?event_id=${eventId}`, scope);
    return response.data?.assigned_count ?? 0;
  }, [eventId]);

  /** Vide toutes les assignations (les heats terminés sont préservés côté serveur). */
  const unassignAllTeams = useCallback(async (): Promise<number> => {
    const response = await apiService.post(`/heats/unassign-all?event_id=${eventId}`, {});
    return response.data?.removed_count ?? 0;
  }, [eventId]);

  /**
   * Équipes enrichies du NOM de leur catégorie.
   *
   * `GET /teams` ne renvoie que `category_id` ; le rail des équipes à placer
   * les groupe et les filtre par nom de catégorie. La jointure se fait donc
   * ici, une fois, plutôt que dans chaque écran qui affiche une équipe.
   */
  const teamsWithCategory = React.useMemo(() => teams.map(team => ({
    ...team,
    category: team.category
      || categories.find(c => c.id === team.category_id)?.name
      || undefined,
  })), [teams, categories]);

  useEffect(() => {
    if (!eventId) return;
    loadHeats();
    loadCategories();
    loadCategoryGroups();
    loadWods();
    loadTeams();
  }, [eventId, loadHeats, loadCategories, loadCategoryGroups, loadWods, loadTeams]);

  // ------------------------------------------------------------------
  // Temps réel (LOG-201)
  //
  // Ce que le terrain modifie — assignations, heats, équipes — doit apparaître
  // sans recharger la page. On ne recharge QUE ce que l'événement concerne :
  // un changement d'équipe ne doit pas relancer la liste des heats, sinon on
  // paie un rechargement complet à chaque tap d'un organisateur.
  //
  // Note : `heat_participant` ne porte pas d'identifiant de heat exploitable
  // ici, donc on recharge tous les participants. C'est le seul cas grossier, et
  // il reste borné à une requête par heat — le même coût que l'ouverture de la
  // page.
  // ------------------------------------------------------------------
  useRealtime(['heat', 'heat_participant', 'team', 'category', 'category_group', 'wod'],
    useCallback((changement) => {
      switch (changement.entity_type) {
        case 'heat':
          loadHeats();
          break;
        case 'heat_participant':
          loadAllParticipants();
          break;
        case 'team':
          loadTeams();
          break;
        case 'category':
          loadCategories();
          break;
        case 'category_group':
          loadCategoryGroups();
          break;
        case 'wod':
          loadWods();
          break;
      }
    }, [loadHeats, loadAllParticipants, loadTeams, loadCategories, loadCategoryGroups, loadWods]));

  return {
    heats, setHeats,
    categories,
    categoryGroups,
    wods,
    teams: teamsWithCategory,
    // `setHeatParticipants` est exposé pour le déplacement optimiste d'une
    // équipe (drag & drop) : l'affichage doit bouger avant l'aller-retour
    // serveur, et le rechargement confirme ensuite.
    heatParticipants, setHeatParticipants,
    loading, setLoading,
    loadHeats,
    loadCategories,
    loadCategoryGroups,
    loadWods,
    loadTeams,
    loadAllParticipants,
    assignTeamToHeat,
    removeTeamFromHeat,
    autoAssignTeams,
    unassignAllTeams,
  };
}
